import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const PHOTOS_BUCKET = 'user-photos';

const PhotoManagementSection = ({ photos, onPhotoUpdate }) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  // Use actual photos from props, or empty array if none
  const [photoList, setPhotoList] = useState(photos || []);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Update photoList when photos prop changes
  useEffect(() => {
    if (photos && Array.isArray(photos)) {
      setPhotoList(photos);
    }
  }, [photos]);

  const privacyLevelOptions = [
  { value: 'high', label: 'High Blur', description: 'Maximum protection' },
  { value: 'medium', label: 'Medium Blur', description: 'Moderate blur' },
  { value: 'low', label: 'Low Blur', description: 'Minimal blur' },
  { value: 'none', label: 'No Blur', description: 'Clear photo' }];


  const handlePhotoUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (!user?.id) {
      alert('You must be logged in to upload photos.');
      return;
    }

    // Check remaining slots
    const remainingSlots = 6 - photoList.length;
    if (files.length > remainingSlots) {
      alert(`You can only upload ${remainingSlots} more photo(s). Maximum 6 photos allowed.`);
      return;
    }

    setShowUploadModal(true);
    setUploadError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const uploadedPhotos = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file. Please select an image.`);
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} is too large. Maximum size is 5MB.`);
        }

        // Upload to Supabase Storage
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}_${i}.${fileExt}`;
        const filePath = `${fileName}`;

        setUploadProgress(Math.round(((i + 0.5) / files.length) * 100));

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(PHOTOS_BUCKET)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
        }

        // Get public URL or signed URL
        const { data: urlData } = supabase.storage
          .from(PHOTOS_BUCKET)
          .getPublicUrl(filePath);

        const photoUrl = urlData?.publicUrl;

        if (!photoUrl) {
          // If public URL doesn't work, try signed URL
          const { data: signedUrlData } = await supabase.storage
            .from(PHOTOS_BUCKET)
            .createSignedUrl(filePath, 3600);

          if (signedUrlData?.signedUrl) {
            uploadedPhotos.push({
              id: Date.now() + i,
              url: signedUrlData.signedUrl,
              alt: `Profile photo ${photoList.length + i + 1}`,
              privacyLevel: 'medium',
              isPrimary: photoList.length === 0 && i === 0, // First photo is primary
              storagePath: filePath
            });
          } else {
            throw new Error(`Failed to get URL for ${file.name}`);
          }
        } else {
          uploadedPhotos.push({
            id: Date.now() + i,
            url: photoUrl,
            alt: `Profile photo ${photoList.length + i + 1}`,
            privacyLevel: 'medium',
            isPrimary: photoList.length === 0 && i === 0, // First photo is primary
            storagePath: filePath
          });
        }

        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      // Update photo list
      const updatedPhotos = [...photoList, ...uploadedPhotos];
      setPhotoList(updatedPhotos);
      
      // Notify parent component
      if (onPhotoUpdate) {
        onPhotoUpdate(updatedPhotos);
      }

      setUploadProgress(100);
      setTimeout(() => {
        setShowUploadModal(false);
        setUploadProgress(0);
        setIsUploading(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 500);

    } catch (error) {
      console.error('Photo upload error:', error);
      setUploadError(error.message || 'Failed to upload photos. Please try again.');
      setIsUploading(false);
      // Keep modal open to show error
    }
  };

  const handleDeletePhoto = async (photoId) => {
    const photoToDelete = photoList.find(p => p.id === photoId);
    if (!photoToDelete) return;

    // Confirm deletion
    if (!confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      // Delete from storage if it has a storagePath
      if (photoToDelete.storagePath) {
        const { error: deleteError } = await supabase.storage
          .from(PHOTOS_BUCKET)
          .remove([photoToDelete.storagePath]);

        if (deleteError) {
          console.error('Storage delete error:', deleteError);
          // Continue with local deletion even if storage delete fails
        }
      }

      // Update local state
      const updatedPhotos = photoList.filter((photo) => photo?.id !== photoId);
      
      // If deleted photo was primary, make first remaining photo primary
      if (photoToDelete.isPrimary && updatedPhotos.length > 0) {
        updatedPhotos[0].isPrimary = true;
      }

      setPhotoList(updatedPhotos);
      setSelectedPhoto(null);

      // Notify parent component
      if (onPhotoUpdate) {
        onPhotoUpdate(updatedPhotos);
      }
    } catch (error) {
      console.error('Delete photo error:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const handleSetPrimary = (photoId) => {
    const updatedPhotos = photoList.map((photo) => ({
      ...photo,
      isPrimary: photo?.id === photoId
    }));
    
    setPhotoList(updatedPhotos);
    
    // Notify parent component
    if (onPhotoUpdate) {
      onPhotoUpdate(updatedPhotos);
    }
  };

  const handlePrivacyChange = (photoId, privacyLevel) => {
    const updatedPhotos = photoList.map((photo) =>
      photo?.id === photoId ? { ...photo, privacyLevel } : photo
    );
    
    setPhotoList(updatedPhotos);
    
    // Notify parent component
    if (onPhotoUpdate) {
      onPhotoUpdate(updatedPhotos);
    }
  };

  const getBlurClass = (privacyLevel) => {
    switch (privacyLevel) {
      case 'high':return 'blur-lg';
      case 'medium':return 'blur-md';
      case 'low':return 'blur-sm';
      default:return '';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-semibold">Photo Management</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Upload and manage your profile photos ({photoList?.length}/6)
          </p>
        </div>
        <Button
          variant="default"
          size="sm"
          iconName="Upload"
          iconPosition="left"
          onClick={handlePhotoUploadClick}
          disabled={photoList?.length >= 6 || isUploading}>
          Upload
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {photoList?.map((photo) =>
        <div
          key={photo?.id}
          className="relative group cursor-pointer"
          onClick={() => setSelectedPhoto(photo)}>

            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-muted">
              <Image
              src={photo?.url}
              alt={photo?.alt}
              className={`w-full h-full object-cover transition-smooth group-hover:scale-105 ${getBlurClass(photo?.privacyLevel)}`} />

            </div>
            {photo?.isPrimary &&
          <div className="absolute top-2 left-2 px-2 py-1 bg-primary text-primary-foreground rounded-md text-xs font-medium flex items-center gap-1">
                <Icon name="Star" size={12} color="#FFFFFF" />
                Primary
              </div>
          }
            <div className="absolute top-2 right-2 px-2 py-1 bg-card/90 rounded-md text-xs font-medium capitalize">
              {photo?.privacyLevel} Blur
            </div>
            <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-smooth flex items-center justify-center gap-2">
              <button
              onClick={(e) => {
                e?.stopPropagation();
                setSelectedPhoto(photo);
              }}
              className="p-2 bg-card rounded-lg hover:bg-muted transition-smooth">

                <Icon name="Edit" size={20} />
              </button>
              <button
              onClick={(e) => {
                e?.stopPropagation();
                handleDeletePhoto(photo?.id);
              }}
              className="p-2 bg-card rounded-lg hover:bg-error/10 hover:text-error transition-smooth">

                <Icon name="Trash2" size={20} />
              </button>
            </div>
          </div>
        )}

        {photoList?.length < 6 &&
        <label
          className="aspect-[3/4] rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-smooth flex flex-col items-center justify-center gap-2 cursor-pointer">
          <Icon name="Plus" size={32} color="var(--color-muted-foreground)" />
          <span className="text-sm text-muted-foreground">Add Photo</span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>
        }
      </div>
      {selectedPhoto &&
      <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-background/80">
          <div className="w-full max-w-2xl bg-card rounded-xl shadow-warm-2xl overflow-hidden animate-scale-in">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-heading font-semibold">Edit Photo</h3>
              <button
              onClick={() => setSelectedPhoto(null)}
              className="p-2 hover:bg-muted rounded-lg transition-smooth">

                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium mb-2">Original Photo</p>
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-muted">
                    <Image
                    src={selectedPhoto?.url}
                    alt={selectedPhoto?.alt}
                    className="w-full h-full object-cover" />

                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Preview with Privacy</p>
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-muted">
                    <Image
                    src={selectedPhoto?.url}
                    alt={selectedPhoto?.alt}
                    className={`w-full h-full object-cover ${getBlurClass(selectedPhoto?.privacyLevel)}`} />

                  </div>
                </div>
              </div>

              <Select
              label="Privacy Level"
              options={privacyLevelOptions}
              value={selectedPhoto?.privacyLevel}
              onChange={(value) => handlePrivacyChange(selectedPhoto?.id, value)}
              description="Control how this photo appears to others" />


              <div className="flex flex-col sm:flex-row gap-3">
                {!selectedPhoto?.isPrimary &&
              <Button
                variant="outline"
                fullWidth
                iconName="Star"
                iconPosition="left"
                onClick={() => {
                  handleSetPrimary(selectedPhoto?.id);
                  setSelectedPhoto(null);
                }}>

                    Set as Primary
                  </Button>
              }
                <Button
                variant="destructive"
                fullWidth
                iconName="Trash2"
                iconPosition="left"
                onClick={() => handleDeletePhoto(selectedPhoto?.id)}>

                  Delete Photo
                </Button>
              </div>
            </div>
          </div>
        </div>
      }
      {showUploadModal &&
      <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-background/80">
          <div className="w-full max-w-md bg-card rounded-xl shadow-warm-2xl p-6 animate-scale-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Upload" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-lg font-heading font-semibold mb-2">
                {uploadError ? 'Upload Failed' : 'Uploading Photo'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {uploadError ? uploadError : 'Please wait while we process your photo...'}
              </p>
            </div>

            {!uploadError && (
              <div className="space-y-2">
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-center text-sm font-medium text-muted-foreground">{uploadProgress}%</p>
              </div>
            )}

            {uploadError && (
              <div className="space-y-3">
                <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
                  <p className="text-sm text-error">{uploadError}</p>
                </div>
                <Button
                  variant="default"
                  fullWidth
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadError(null);
                    setUploadProgress(0);
                    setIsUploading(false);
                  }}
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      }
    </div>);

};

export default PhotoManagementSection;